/**
 * QuoteFileUpload – drag-and-drop + click-to-browse file upload component.
 *
 * Usage:
 *   <quote-file-upload
 *     data-max-size="10485760"
 *     data-accepted=".jpg,.jpeg,.png,.pdf"
 *     data-upload-url="https://product-configure.monkeydo.it/api/upload-file"
 *     data-shop-name="sole-zonwering"
 *   >
 *     <!-- inner markup rendered server-side -->
 *   </quote-file-upload>
 */
class QuoteFileUpload extends HTMLElement {
  static MAX_SIZE_DEFAULT = 10 * 1024 * 1024; // 10 MB
  static ACCEPTED_DEFAULT = '.jpg,.jpeg,.png,.pdf';

  /** @type {File[]} */
  #files = [];

  /** @type {string[]} Uploaded file URLs returned by the API */
  #uploadedUrls = [];

  /** @type {boolean} */
  #isUploading = false;

  /** @type {number} */
  maxSize = QuoteFileUpload.MAX_SIZE_DEFAULT;

  /** @type {string[]} */
  accepted = QuoteFileUpload.ACCEPTED_DEFAULT.split(',');

  /** @type {string} */
  uploadUrl = '';

  /** @type {string} */
  shopName = '';

  /** @type {string} */
  cdnBase = '';

  /** @type {HTMLDivElement | null} */
  dropzone = null;

  /** @type {HTMLInputElement | null} */
  fileInput = null;

  /** @type {HTMLDivElement | null} */
  fileList = null;

  /** @type {HTMLElement | null} */
  errorEl = null;

  /** @type {HTMLInputElement | null} */
  hiddenInput = null;

  connectedCallback() {
    this.maxSize = parseInt(this.dataset.maxSize || '', 10) || QuoteFileUpload.MAX_SIZE_DEFAULT;
    this.accepted = (this.dataset.accepted || QuoteFileUpload.ACCEPTED_DEFAULT)
      .split(',')
      .map((e) => e.trim().toLowerCase());
    this.uploadUrl = this.dataset.uploadUrl || '';
    this.shopName = this.dataset.shopName || '';
    this.cdnBase = this.dataset.cdnBase || '';

    this.dropzone = this.querySelector('[data-dropzone]');
    this.fileInput = this.querySelector('input[data-file-input]');
    this.fileList = this.querySelector('[data-file-list]');
    this.errorEl = this.querySelector('[data-upload-error]');
    this.hiddenInput = this.querySelector('input[data-upload-urls]');

    this.#bindEvents();
  }

  /* ------------------------------------------------------------------ */
  /*  Event binding                                                      */
  /* ------------------------------------------------------------------ */

  #bindEvents() {
    if (!this.dropzone || !this.fileInput || !this.fileList) return;

    const dropzone = this.dropzone;
    const fileInput = this.fileInput;
    const fileListEl = this.fileList;

    // Click dropzone to open file picker
    dropzone.addEventListener('click', () => fileInput.click());

    // File input change
    fileInput.addEventListener('change', () => {
      if (fileInput.files) {
        this.#addFiles(Array.from(fileInput.files));
      }
      fileInput.value = ''; // reset so same file can be re-selected
    });

    // Drag events
    dropzone.addEventListener('dragover', (/** @type {DragEvent} */ e) => {
      e.preventDefault();
      dropzone.classList.add('quote-upload__dropzone--active');
    });
    dropzone.addEventListener('dragleave', () => {
      dropzone.classList.remove('quote-upload__dropzone--active');
    });
    dropzone.addEventListener('drop', (/** @type {DragEvent} */ e) => {
      e.preventDefault();
      dropzone.classList.remove('quote-upload__dropzone--active');
      if (e.dataTransfer?.files) {
        this.#addFiles(Array.from(e.dataTransfer.files));
      }
    });

    // Remove file buttons (delegated)
    fileListEl.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      const btn = target.closest('[data-remove-file]');
      if (!(btn instanceof HTMLElement)) return;
      const index = parseInt(btn.dataset.removeFile || '0', 10);
      this.#removeFile(index);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  File management                                                    */
  /* ------------------------------------------------------------------ */

  /** @param {File[]} newFiles */
  #addFiles(newFiles) {
    this.#clearError();

    for (const file of newFiles) {
      // Validate type
      const nameParts = file.name.split('.');
      const ext = '.' + (nameParts.pop() || '').toLowerCase();
      if (!this.accepted.includes(ext)) {
        this.#showError(
          this.dataset.errorType?.replace('{{ file }}', file.name) ||
            `"${file.name}" is niet toegestaan. Alleen JPG, PNG en PDF.`
        );
        continue;
      }

      this.#files.push(file);
    }

    // Validate total size
    const totalSize = this.#files.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > this.maxSize) {
      this.#showError(
        this.dataset.errorSize ||
          `De totale bestandsgrootte mag maximaal ${this.#formatBytes(this.maxSize)} zijn.`
      );
      // Remove files that were just added that caused overflow
      while (
        this.#files.length > 0 &&
        this.#files.reduce((s, f) => s + f.size, 0) > this.maxSize
      ) {
        this.#files.pop();
      }
    }

    this.#render();
  }

  /** @param {number} index */
  #removeFile(index) {
    this.#files.splice(index, 1);
    this.#clearError();
    this.#render();
  }

  /* ------------------------------------------------------------------ */
  /*  Rendering                                                          */
  /* ------------------------------------------------------------------ */

  #render() {
    if (!this.fileList) return;

    this.fileList.innerHTML = '';

    this.#files.forEach((file, i) => {
      const item = document.createElement('div');
      item.className = 'quote-upload__file';

      const thumb = document.createElement('div');
      thumb.className = 'quote-upload__thumb';

      if (file.type.startsWith('image/')) {
        const img = document.createElement('img');
        img.src = URL.createObjectURL(file);
        img.alt = file.name;
        img.onload = () => URL.revokeObjectURL(img.src);
        thumb.appendChild(img);
      } else {
        thumb.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;
      }

      const info = document.createElement('div');
      info.className = 'quote-upload__file-info';
      info.innerHTML = `
        <span class="quote-upload__file-name">${this.#escapeHtml(file.name)}</span>
        <span class="quote-upload__file-size">${this.#formatBytes(file.size)}</span>
      `;

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'quote-upload__remove';
      removeBtn.dataset.removeFile = String(i);
      removeBtn.setAttribute('aria-label', `${file.name} verwijderen`);
      removeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

      item.appendChild(thumb);
      item.appendChild(info);
      item.appendChild(removeBtn);
      /** @type {HTMLDivElement} */ (this.fileList).appendChild(item);
    });

    // Update total size indicator
    const totalEl = this.querySelector('[data-total-size]');
    if (totalEl) {
      const total = this.#files.reduce((s, f) => s + f.size, 0);
      totalEl.textContent = this.#files.length
        ? `${this.#files.length} bestand${this.#files.length === 1 ? '' : 'en'} — ${this.#formatBytes(total)} / ${this.#formatBytes(this.maxSize)}`
        : '';
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Upload to external API                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Uploads selected files. Returns a promise that resolves with the API
   * response (or null when no files selected).
   * @returns {Promise<any>}
   */
  async uploadFiles() {
    if (this.#files.length === 0) return null;
    if (!this.uploadUrl) {
      console.warn('[QuoteFileUpload] No upload URL configured.');
      return null;
    }

    this.#isUploading = true;
    this.#setLoadingState(true);
    this.#uploadedUrls = [];

    try {
      // Upload each file individually — the API processes one file per request
      for (const file of this.#files) {
        const formData = new FormData();
        formData.append('files[]', file);
        formData.append('shop_name', this.shopName);

        const response = await fetch(this.uploadUrl, {
          method: 'POST',
          body: formData,
        });

        const rawText = await response.text();
        console.log(`[QuoteFileUpload] "${file.name}" → ${rawText} (status ${response.status})`);

        const filename = rawText.trim();
        if (filename) {
          this.#uploadedUrls.push(this.cdnBase + filename);
        }
      }

      console.log('[QuoteFileUpload] All file URLs:', this.#uploadedUrls);

      // Write URLs into hidden input so Shopify includes them in the contact email
      if (this.hiddenInput && this.#uploadedUrls.length) {
        this.hiddenInput.value = this.#uploadedUrls.join(', ');
      }

      return this.#uploadedUrls;
    } catch (err) {
      console.error('[QuoteFileUpload] Upload failed:', err);
      this.#showError(
        this.dataset.errorUpload || 'Er ging iets mis bij het uploaden. Probeer het opnieuw.'
      );
      throw err;
    } finally {
      this.#isUploading = false;
      this.#setLoadingState(false);
    }
  }

  /** @returns {boolean} Whether an upload is currently in progress */
  get isUploading() {
    return this.#isUploading;
  }

  /** @returns {boolean} Whether files have been selected */
  get hasFiles() {
    return this.#files.length > 0;
  }

  /* ------------------------------------------------------------------ */
  /*  UI helpers                                                         */
  /* ------------------------------------------------------------------ */

  /** @param {boolean} loading */
  #setLoadingState(loading) {
    if (this.dropzone) {
      this.dropzone.classList.toggle('quote-upload__dropzone--loading', loading);
    }
    const spinner = /** @type {HTMLElement | null} */ (this.querySelector('[data-upload-spinner]'));
    if (spinner) spinner.hidden = !loading;
  }

  /** @param {string} msg */
  #showError(msg) {
    if (this.errorEl) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }
  }

  #clearError() {
    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }
  }

  /** @param {number} bytes */
  #formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /** @param {string} str */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('quote-file-upload', QuoteFileUpload);
